import { Image } from '@custom-types/Image';
import { Query } from '@custom-types/Query';
import { Response } from '@custom-types/Response';
import { Check } from '@pages/Check/service';
import apiFn from '@services/api-service';

export type Person = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  profileImage?: Image;
  checkins: Check[];
  checkinCode?: string;
  companyName?: string;
  title?: string;
  groups?: { id: string; name: string }[];
  pronouns?: string;
};

enum Include {
  ProfileImage = 'profile_image',
  Checks = 'checkins',
  Email = 'email',
  Groups = 'groups',
}

const DEFAULT_INCLUDES = [Include.ProfileImage, Include.Email, Include.Checks, Include.Groups];

const api = (eventId: string) => apiFn(`/api/uapi/events/${eventId}/people`);
const apiKiosk = (eventId: string) =>
  apiFn(`/api/uapi/events/${eventId}/onsite-app/kiosk/people`, {
    Accept: 'application/vnd.eventmobi+json; version=p.9',
  });

export const list = (eventId: string, query: Partial<Query>) => {
  const params = {
    limit: 20,
    include: DEFAULT_INCLUDES.join(','),
    ...(query.filter && { checkin_status: query.filter }),
    ...(query.sessionId && { checkin_session_id: query.sessionId }),
    search: query.search,
    sort: query.sort,
    page: query.page,
  };

  return api(eventId)
    .get('', { params })
    .then((response: Response<Person[]>) => response.data);
};

export const getKiosk = (eventId: string, search: string, page = 0) =>
  apiKiosk(eventId)
    .get('', { params: { search, page, limit: 20 } })
    .then((response: Response<Person[]>) => response.data);

export const get = (eventId: string, id: string) =>
  api(eventId)
    .get(id, { params: { include: DEFAULT_INCLUDES.join(',') } })
    .then((response: Response<Person>) => response.data.data);

export const getByCheckinCode = (eventId: string, checkinCode: string) =>
  api(eventId)
    .get('', { params: { checkin_code: checkinCode } })
    .then((response: Response<Person[]>) => response.data.data);

export const getCurrentUser = (eventId: string) =>
  api(eventId)
    .get('me')
    .then((response: Response<Person>) => response.data.data);
